/**
 * システム全体のハードリミット (SPEC 26, 27, 62, 63)。
 *
 * 問題ごとの time_limit_ms / memory_limit_mb はここで定義した上限で必ずクランプする。
 * 「問題の設定値だけでシステム全体の安全性を突破できないようにする」(SPEC 26) ための
 * 唯一の防壁なので、この値を参照せずに問題設定をそのまま Docker へ渡してはならない。
 */
export const SYSTEM_LIMITS = {
    /** 提出ソースコードの最大バイト数。DB の CHECK 制約と同値。 */
    MAX_SOURCE_CODE_BYTES: 64 * 1024,
    /** 1 testcase あたりの実行時間上限（ミリ秒）。問題設定より優先される。 */
    MAX_TIME_LIMIT_MS: 30_000,
    MIN_TIME_LIMIT_MS: 100,
    /** コンパイルの実時間上限。無限にテンプレート展開する攻撃を止める。 */
    MAX_COMPILE_TIME_MS: 20_000,
    /** メモリ上限（MiB）。 */
    MAX_MEMORY_LIMIT_MB: 1024,
    MIN_MEMORY_LIMIT_MB: 16,
    /** コンパイル時のメモリ上限。実行時上限とは別枠。 */
    COMPILE_MEMORY_LIMIT_MB: 1024,
    /** stdout の最大バイト数。超えたら OUTPUT_LIMIT_EXCEEDED (SPEC 27)。 */
    MAX_OUTPUT_BYTES: 10 * 1024 * 1024,
    /** stderr は診断用に少しだけ拾う。ユーザーへ返すのはここまで。 */
    MAX_STDERR_BYTES: 8 * 1024,
    /** コンパイラ出力としてユーザーへ返す最大文字数。 */
    MAX_COMPILE_OUTPUT_CHARS: 16 * 1024,
    /** sandbox 内のプロセス数上限。fork bomb 対策。 */
    MAX_PROCESSES: 64,
    /** sandbox 内で作成できるファイルサイズ上限（バイト）。 */
    MAX_FILE_SIZE_BYTES: 64 * 1024 * 1024,
    /** Custom Test の入力上限 (SPEC 33)。 */
    MAX_CUSTOM_TEST_INPUT_BYTES: 256 * 1024,
    /** 1 提出あたりの testcase 数上限。判定が無限に伸びるのを防ぐ。 */
    MAX_TESTCASES_PER_PROBLEM: 500,
};
/**
 * 問題設定値をシステム上限内に丸める。
 * worker が Docker へ渡す直前に必ず通すこと。
 */
export function clampTimeLimitMs(problemTimeLimitMs, languageFactor = 1) {
    const scaled = Math.round(problemTimeLimitMs * languageFactor);
    return Math.min(Math.max(scaled, SYSTEM_LIMITS.MIN_TIME_LIMIT_MS), SYSTEM_LIMITS.MAX_TIME_LIMIT_MS);
}
export function clampMemoryLimitMb(problemMemoryLimitMb, languageFactor = 1) {
    const scaled = Math.round(problemMemoryLimitMb * languageFactor);
    return Math.min(Math.max(scaled, SYSTEM_LIMITS.MIN_MEMORY_LIMIT_MB), SYSTEM_LIMITS.MAX_MEMORY_LIMIT_MB);
}
/**
 * レート制限 (SPEC 62)。
 * 提出と Custom Test は特に厳しくする。
 */
export const RATE_LIMITS = {
    SUBMISSION: { points: 1, durationSec: 5, burst: 100, burstDurationSec: 3600 },
    CUSTOM_TEST: { points: 1, durationSec: 10, burst: 50, burstDurationSec: 3600 },
    LOGIN: { points: 10, durationSec: 300 },
    SIGNUP: { points: 5, durationSec: 3600 },
    API_DEFAULT: { points: 300, durationSec: 60 },
    DISCUSSION: { points: 10, durationSec: 600 },
    /**
     * 自動作問。1 件がモデル呼び出しと sandbox 実行を伴うので、
     * 提出よりずっと重い。運営向けの機能だが上限は必要。
     */
    PROBLEM_GENERATION: { points: 10, durationSec: 3600 },
};
/**
 * アバター画像。
 *
 * 値は Supabase Storage のバケット設定（migration 0013）と一致させること。
 * 片方だけ変えると、画面では通るのにアップロードで弾かれる。
 */
export const AVATAR_LIMITS = {
    MAX_BYTES: 2 * 1024 * 1024,
    /** SVG は script を含められるので受け取らない。 */
    ALLOWED_TYPES: ['image/png', 'image/jpeg', 'image/webp'],
    BUCKET: 'avatars',
};
/** ページネーション上限 (SPEC 61)。 */
export const PAGINATION = {
    DEFAULT_PER_PAGE: 20,
    MAX_PER_PAGE: 100,
};
//# sourceMappingURL=limits.js.map